import { checkErrors } from 'shared/validations';
import { config } from 'config';
import { documentTypeOptions } from 'shared/fixtures';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { optionActions } from 'core/Option';
import { patientActions } from 'core/Patient';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import validator from './fixtures/validations';
import {
    AddressDetailsContainer,
    BtnContainer,
    ColStyled,
    RowStyled
} from './styled';
import {
    Checkbox,
    CheckboxGroup,
    DateSelect,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    OptionGroup,
    SelectSearch,
    Text
} from 'mol-ui';
import {
    CityPicker,
    CoveragePicker,
    DistrictPicker,
    DoctorPicker,
    GenderPicker,
    OfficePicker,
    PlanPicker,
    ProvincePicker,
    SpecialtyPicker
} from 'views/pickers';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ErrorMessage,
    FormLayout,
    NotificationModal,
    PhoneInput
} from 'views/components';
import React, { useCallback, useEffect, useState } from 'react';
import {
    dateToObject,
    objectToDate
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';
const {
    defaultPhone,
    patientIdentificationAlpha,
    patientRegistration,
    specialtyPin
} = config;

const PinRegistration = () => {

    const DEFAULT_FORM = {
        birth_date: {
            day: '',
            month: '',
            year: ''
        },
        building_number: '',
        cellphone_number: '',
        city_id: null,
        district_id: null,
        doctor_id: 0,
        door_number: '',
        email: '',
        first_name: '',
        floor: '',
        gender: 'Female',
        health_insurance: '',
        identification: '',
        identification_id: 1,
        last_name: '',
        medical_coverage_id: undefined,
        particular: 1,
        plan_id: undefined,
        province_id: null,
        specialty_id: 0,
        street: ''
    };

    const dispatch = useDispatch();
    const { push } = useHistory();
    const { t } = useTranslation();
    const { symptoms: allSymptoms } = useSelector('option');
    const {
        pin,
        single: singlePatient,
        singleLoading: singlePatientLoading
    } = useSelector('patient');

    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const [form,, setFormValue, setFormValues] = useObjectState(DEFAULT_FORM);
    const [loadPatient, setLoadPatient] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const [symptoms, setSymptoms] = useState([]);
    const [showAddressDetails, setShowAddressDetails] = useState(false);

    useEffect(() => {
        dispatch(optionActions.getProvinces());
    }, []);

    const clearSymptoms = useCallback(() => setSymptoms(symptoms =>
        symptoms.map(symptom => ({ ...symptom, value: false }))
    ), [setSymptoms]);

    useEffect(() => {
        dispatch(optionActions.getSymptoms());
        clearSymptoms();
    }, [clearSymptoms]);

    useEffect(() => {
        setSymptoms([...allSymptoms.map(symptom => ({ ...symptom, value: false }))]);
    }, [allSymptoms]);

    useEffect(() => {
        if (!isEmpty(singlePatient) && loadPatient) {
            const {
                birth_date,
                cellphone_number,
                building_number,
                city_id,
                district_id,
                door_number,
                email,
                first_name,
                floor,
                gender,
                health_insurance,
                identification,
                last_name,
                medical_coverage_id,
                plan_id,
                province_id,
                street
            } = singlePatient;
            setLoadPatient(false);
            clearErrors();
            (!!province_id || !!street) && setShowAddressDetails(true);
            setFormValues({
                birth_date: !!birth_date ? dateToObject(moment(birth_date)) : {},
                building_number,
                cellphone_number,
                city_id,
                district_id,
                door_number,
                email,
                first_name,
                floor,
                gender,
                health_insurance: health_insurance ?? '',
                identification,
                last_name,
                medical_coverage_id,
                particular: !medical_coverage_id,
                plan_id,
                province_id,
                street
            });
        }
    }, [loadPatient, setFormValues, setLoadPatient, singlePatient]);

    const changePin = () => {
        dispatch(patientActions.setPatientPin(null));
        push(t('/dashboard'));
    };

    const validate = label => value => setErrors(checkErrors(label, value, errors, validator));

    const handleChange = (id, value) => {
        clearError(id);
        const { particular } = form;
        if (!!particular) {
            clearError('health_insurance');
            clearError('medical_coverage_id');
            clearError('plan_id');
        }
        if (id === 'specialty_id') {
            setFormValue('doctor_id', 0);
        }
        if (id === 'province_id') {
            setFormValue('city_id', null);
            setFormValue('district', false);
            setFormValue('district_id', null);
        }
        if (id === 'city_id') {
            setFormValue('district', false);
            setFormValue('district_id', null);
        }
        setFormValue(id, value);
    };

    const handleCheckbox = (value, name) => setFormValue(name, value);

    const handleCoverageRadio = value => {
        if (!!value) {
            clearError('health_insurance');
            clearError('medical_coverage_id');
            clearError('plan_id');
        }
        setFormValue('particular', value);
    };

    const handleGetPatientData = () => {
        const { identification } = form;
        if (identification) {
            setLoadPatient(true);
            const params = {
                search: identification
            };
            dispatch(patientActions.getPatientSingle(params));
        }
    };

    const handleSubmit = () => {
        const symptomsIds = symptoms.filter(symptom => !!symptom.value).map(symptom => symptom.id);
        const { birth_date, health_insurance, medical_coverage_id, particular, plan_id } = form;
        const errors = {
            ...validator.checkErrors({ ...form, symptomsIds }),
            ...(!particular && {
                ...(!health_insurance && { health_insurance: t('Requerido') }),
                ...(!medical_coverage_id && { medical_coverage_id: t('Requerido') }),
                ...(!plan_id && { plan_id: t('Requerido') })
            })
        };

        if (!isEmpty(errors)) {
            setErrors(errors);
            setSubmitFailed(true);
            return;
        }
        setSubmitFailed(false);

        const patientUpdateDTO = {
            ...form,
            birth_date: objectToDate(birth_date).format('YYYY-MM-DD')
        };

        if (!specialtyPin || patientUpdateDTO.specialty_id === 0) {
            delete patientUpdateDTO.specialty_id;
        }
        if (!specialtyPin || patientUpdateDTO.doctor_id === 0) {
            delete patientUpdateDTO.doctor_id;
        }

        const params = {
            ...patientUpdateDTO,
            symptoms: symptomsIds
        };

        dispatch(patientActions.pinRegistration(params));
    };

    const handleSymptomSelection = ({ id }) => {
        const result = [...symptoms];
        const symptomIndex = symptoms.findIndex(symptom => symptom.id === id);
        result[symptomIndex] = { ...result[symptomIndex], value: !result[symptomIndex].value };
        const symptomsIds = result.filter(symptom => !!symptom.value).map(symptom => symptom.id);
        validate('symptomsIds')(symptomsIds);
        setSymptoms(result);
    };

    return (
        <FormLayout goBackTo={ t('/dashboard') } title={ t('Crear PIN') }>
            <Row>
                <Col md="12" xl={{ offset: 2, size: 8 }} >
                    <Row>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Nombre') }>
                                <Input
                                    error={ errors.first_name }
                                    value={ form.first_name }
                                    onChange={ value => handleChange('first_name', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup error={ errors.last_name } label="Apellido">
                                <Input
                                    error={ errors.last_name }
                                    value={ form.last_name }
                                    onChange={ value => handleChange('last_name', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3" sm="12">
                            <FormGroup label={ t('Tipo') } marginBottom="10px">
                                <SelectSearch
                                    hasError={ !!errors.identification_id }
                                    options={ documentTypeOptions }
                                    value={ form.identification_id }
                                    width="100%"
                                    onChange={ value => handleChange('identification_id', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3" sm="12">
                            <FormGroup label={ t('N° de documento') }>
                                <Input
                                    disabled={ singlePatientLoading }
                                    error={ errors.identification }
                                    type={ patientIdentificationAlpha ? 'text' : 'number' }
                                    value={ form.identification }
                                    onChange={ value => handleChange('identification', value) }
                                    onEnterPress={ handleGetPatientData }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Fecha de nacimiento') } marginBottom="10px">
                                <DateSelect
                                    error={ submitFailed ? errors.birth_date : '' }
                                    hasError={ submitFailed && !!errors.birth_date }
                                    value={ form.birth_date }
                                    onChange={ value => handleChange('birth_date', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Sexo') }>
                                <GenderPicker
                                    margin="0px 0px 20px 0px"
                                    value={ form.gender }
                                    onChange={ value => handleChange('gender', value) }
                                />
                                <ErrorMessage error={ errors.gender } />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Email') }>
                                <Input
                                    error={ errors.email }
                                    value={ form.email }
                                    onChange={ value => handleChange('email', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Celular') }>
                                <PhoneInput
                                    margin="0px 0px 20px 0px"
                                    value={ form.cellphone_number ?? defaultPhone.code }
                                    onChange={ value => handleChange('cellphone_number', value) }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {
                            !showAddressDetails &&
                                <AddressDetailsContainer>
                                    <IconButton
                                        icon="add_circle_outline"
                                        size={ 22 }
                                        onClick={ setShowAddressDetails }
                                    />
                                    <Text>
                                        { t('Agregar datos de domicilio') }
                                    </Text>
                                </AddressDetailsContainer>
                        }
                        {
                            !!showAddressDetails &&
                                <>
                                    <Col md="6" xs="12">
                                        <FormGroup label={ t('Dirección') }>
                                            <Input
                                                placeholder={ t('Ingrese una dirección') }
                                                value={ form.street }
                                                onChange={ value => handleChange('street', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label="N°">
                                            <Input
                                                value={ form.building_number }
                                                onChange={ value => handleChange('building_number', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label={ t('Piso') }>
                                            <Input
                                                value={ form.floor }
                                                onChange={ value => handleChange('floor', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label={ t('Puerta') }>
                                            <Input
                                                value={ form.door_number }
                                                onChange={ value => handleChange('door_number', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Provincia') } margin="0px 0px 20px 0px">
                                            <ProvincePicker
                                                value={ form.province_id }
                                                width="100%"
                                                onChange={ value => handleChange('province_id', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Ciudad') } margin="0px 0px 20px 0px">
                                            <CityPicker
                                                objectChange
                                                params={ form.province_id }
                                                value={ form.city_id }
                                                width="100%"
                                                onChange={ ({ has_district, id }) => {
                                                    handleChange('city_id', id);
                                                    handleChange('district', !!has_district);
                                                } }
                                            />
                                        </FormGroup>
                                    </Col>
                                    {
                                        form.district &&
                                        <Col md="6" sm="12">
                                            <FormGroup label={ t('Barrio') } margin="0px 0px 20px 0px">
                                                <DistrictPicker
                                                    params={ form.city_id }
                                                    value={ form.district_id }
                                                    width="100%"
                                                    onChange={ value => handleChange('district_id', value) }
                                                />
                                            </FormGroup>
                                        </Col>
                                    }
                                </>
                        }
                        <ColStyled sm="6">
                            <FormGroup label={ t('Forma de Atención') }>
                                <OptionGroup
                                    bordered
                                    margin="0px 0px 10px 0px"
                                    options={
                                        [
                                            { label: t('Particular'), value: true },
                                            { label: t('Cobertura médica'), value: false }
                                        ]
                                    }
                                    value={ !!form.particular }
                                    width="100%"
                                    onChange={ value => handleCoverageRadio(!!value ? 1 : 0) }
                                />
                            </FormGroup>
                        </ColStyled>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Consultorio') } >
                                <OfficePicker
                                    hasError={ !!errors.office_id }
                                    value={ form.office_id }
                                    onChange={ value => handleChange('office_id', value) }
                                />
                                <ErrorMessage
                                    relative
                                    error={ errors.office_id }
                                    marginLeft={ 0 }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        !form.particular &&
                        <RowStyled marginbottom="10px">
                            <Col md="4" sm="12">
                                <FormGroup label={ t('Cobertura') }>
                                    <CoveragePicker
                                        hasError={ !!errors.medical_coverage_id }
                                        value={ form.medical_coverage_id }
                                        onChange={ value => handleChange('medical_coverage_id', value) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup label={ t('Plan') }>
                                    <PlanPicker
                                        hasError={ !!errors.plan_id }
                                        params={ form.medical_coverage_id }
                                        value={ form.plan_id }
                                        onChange={ value => handleChange('plan_id', value) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup label={ t('N° de afiliado') }>
                                    <Input
                                        error={ errors.health_insurance }
                                        value={ form.health_insurance }
                                        onChange={ value => handleChange('health_insurance', value) }
                                    />
                                </FormGroup>
                            </Col>
                        </RowStyled>
                    }
                    {
                        specialtyPin &&
                        <RowStyled marginbottom="10px">
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Especialidad') } marginBottom="10px">
                                    <SpecialtyPicker
                                        hasError={ !!errors.specialty_id }
                                        value={ form.specialty_id }
                                        onChange={ value => handleChange('specialty_id', value) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Profesional') }>
                                    <DoctorPicker
                                        callType="patient"
                                        hasError={ !!errors.doctor_id }
                                        specialty={ form.specialty_id }
                                        value={ form.doctor_id }
                                        onChange={ value => handleChange('doctor_id', value) }
                                    />
                                </FormGroup>
                            </Col>
                        </RowStyled>
                    }
                    <Row>
                        <ColStyled sm="12">
                            <FormGroup label={ t('Síntomas del paciente') }>
                                <CheckboxGroup
                                    columns={{
                                        lg: 3,
                                        md: 2,
                                        xs: 1
                                    }}
                                    options={ symptoms }
                                    onClick={ handleSymptomSelection }
                                />
                                <ErrorMessage
                                    relative
                                    error={ errors.symptomsIds }
                                    marginBottom={ 10 }
                                    marginLeft={ 0 }
                                    marginTop={ 10 }
                                />
                            </FormGroup>
                        </ColStyled>
                        {
                            !!patientRegistration &&
                            <ColStyled sm="12">
                                <FormGroup label={ t('Acciones adicionales') }>
                                    <RowStyled>
                                        <Col sm="12">
                                            <Checkbox
                                                checked={ !!form.grant_access }
                                                label={ t('Permitir ingresar desde el perfil de pacientes') }
                                                size={ 14 }
                                                onClick={ () => handleCheckbox(!form.grant_access, 'grant_access') }
                                            />
                                        </Col>
                                    </RowStyled>
                                </FormGroup>
                            </ColStyled>
                        }
                        <BtnContainer>
                            <ExpandButton
                                caption={ t('Confirmar') }
                                loading={ false }
                                size={ 130 }
                                onClick={ handleSubmit }
                            />
                        </BtnContainer>
                    </Row>
                </Col>
            </Row>
            <NotificationModal
                singleButton
                active={ !!pin }
                firstLine={ t('El PIN generado podrá ser ingresado desde el perfil de paciente') }
                height="auto"
                padding="20px 35px"
                title={ pin ? pin.toString() : '' }
                onBack={ changePin }
            />
        </FormLayout>
    );
};

PinRegistration.propTypes = {

};

export default PinRegistration;
